import { Building2Icon, Calendar1Icon, CheckCheckIcon, CircleXIcon, ClipboardClockIcon, CopyXIcon, EqualApproximatelyIcon, FileCheck, FileTextIcon, LogOutIcon, LucideUserCog2, MailCheckIcon, MessageCircleXIcon, PanelTopCloseIcon, Upload, UploadIcon, Eye, EyeClosed, KeyRound, Wallet2Icon, WatchIcon, X, XIcon } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { DocumentInfo, fetchUser, fetchUserWallets, sendDocument, setModalDocument, setSendDocumentStatus, UserInfo } from "../../../store/UserSlice";
import { AuthService } from "../../../services/AuthService";
import { openToast } from "../../../store/ToastSlice";
import "./userProfile.scss";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { SendDocumentDto, UserDocumentStatus } from "../../../models/User";
import { formatCurrencyBRL, formatCurrencyBRL4 } from "../../../utils/format";
import { formatAssetAmount } from "../../../utils/decimal";
import { useNavigate } from "react-router-dom";
import { AuthLogout } from "../../../store/AuthSlice";

const UserProfile : FunctionComponent = () => {

    const disaptch = useAppDispatch();
    const navigate = useNavigate();

    const userInfo: UserInfo            = useAppSelector( (state) => state.user.userInfo )
    const wallets                       = useAppSelector( (state) => state.user.wallets )
    const modalDocumentStatus : boolean = useAppSelector( (state) => state.user.modalDocumentStatus)

    // A tela le state.user.wallets mas nao carregava nada: quem buscava era so
    // o home do cliente. Aberta direto pela URL (ou apos um refresh) a secao
    // "Carteiras" ficava vazia mesmo com carteira existindo no banco.
    useEffect(() => {
        if (userInfo?.id) disaptch(fetchUserWallets(userInfo.id));
    }, [disaptch, userInfo?.id]);

    // ---- Troca de senha
    const [passwordForm, setPasswordForm]       = useState({ current: '', next: '', confirm: '' });
    const [passwordError, setPasswordError]     = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    // Um estado por campo: conferir a senha atual nao deve revelar a nova.
    const [showCurrent, setShowCurrent]       = useState(false);
    const [showNext, setShowNext]             = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);

    // Mesma regra do backend (PASSWORD_REGEX): min 8, com letra e digito.
    const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');

        if (!passwordForm.current || !passwordForm.next) {
            setPasswordError('Preencha a senha atual e a nova senha.');
            return;
        }
        if (!PASSWORD_REGEX.test(passwordForm.next)) {
            setPasswordError('A nova senha precisa ter no minimo 8 caracteres, com ao menos uma letra e um numero.');
            return;
        }
        if (passwordForm.next !== passwordForm.confirm) {
            setPasswordError('As senhas nao conferem.');
            return;
        }

        setChangingPassword(true);
        try {
            await AuthService.changePassword(passwordForm.current, passwordForm.next);
            setPasswordForm({ current: '', next: '', confirm: '' });
            setPasswordSuccess('Senha alterada com sucesso.');
            disaptch(openToast({
                title:   'Senha alterada',
                message: 'Sua nova senha ja esta valendo. Use ela no proximo login.',
                effect:  'success',
            }));
            // Recarrega o userInfo para a flag `usingDefaultPassword` sumir.
            disaptch(fetchUser());
        } catch (e: any) {
            const status = e?.response?.status ?? e?.statusCode;
            const msg =
                status === 401 ? 'Senha atual incorreta.'
                : status === 429 ? 'Muitas tentativas. Aguarde um minuto e tente de novo.'
                : (e?.response?.data?.message ?? e?.message ?? 'Nao foi possivel alterar a senha.');
            setPasswordError(msg);
            disaptch(openToast({ title: 'Falha ao alterar senha', message: msg, effect: 'error' }));
        } finally {
            // No `finally` de proposito: se a request falhar, o botao precisa
            // voltar a funcionar para a pessoa tentar de novo.
            setChangingPassword(false);
        }
    };

    const checkDocumentStatus = (doc: DocumentInfo) => {

        if (doc) {
            if (doc.status === UserDocumentStatus.VERIFIED)
                return (
                    <div className="right flex success">
                        <CheckCheckIcon size={16} className="icon success" />
                        Verificado
                    </div>
                );
    
            if (doc.status === UserDocumentStatus.WAITING)
                return (
                    <div className="right flex warning">
                        <WatchIcon size={16} className="icon warning" />
                        Aguardando
                    </div>
                );
    
            if (doc.status === UserDocumentStatus.REJECTED)
                return (
                    <div className="right flex error">
                        <CopyXIcon size={16} className="icon error" />
                        Rejeitado
                    </div>
                );
    
            if (doc.status === UserDocumentStatus.PENDING)
                return (
                    <div className="right flex pending">
                        <ClipboardClockIcon size={16} className="icon pending" />
                        Pendente
                    </div>
                );
        }

    };

    const isActive = (status:boolean) => {
        if (status) return "ativo";
        return "inativo";
    }

    const logout = () => {
        disaptch( AuthLogout() )
        navigate("/");
    }

    const toggleModal = () => {
        disaptch(setModalDocument(!modalDocumentStatus))
    }

    const [documentNumber, setDocumentNumber]       = useState("");
    const [documentType, setDocumentType]           = useState("");
    const [fileBase64, setFileBase64]               = useState("");
    const [fileName, setFileName]                   = useState("");
    const [sendModalStatus, setSendModalStatus]     = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);


    const sendDocumentResponse = useAppSelector( (state) => state.user.sendDocumentResponse)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            console.error('error file')
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setFileBase64(reader.result as string);
            setFileName(file.name);
        };
        reader.readAsDataURL(file);
    }

    const clearFile = () => {
        setFileBase64("");
        setFileName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (documentNumber.trim() && documentType && fileBase64) {

            setSendModalStatus(true);
            const data : SendDocumentDto = { documentNumber: documentNumber.trim(), documentType, fileBase64, fileName };
            disaptch( sendDocument(data) )

        };
    }

    useEffect( () => {

        if (sendDocumentResponse) {
            disaptch(setSendDocumentStatus(false));
            setDocumentNumber('');
            setDocumentType('');
            setFileBase64('');
            setFileName('');
        }
    }, [sendDocumentResponse])

    return (
        <div className="user-profile">
            
            <div className="content">
                
                <h1>
                    Perfil
                    <small>Informacoes do usuario, carteiras e organizacoes que participa.</small>
                </h1>

                <div className="row">
                    <div className="user-profile-card">
                        <div className="gradient-purple"></div>
                        <div className="card-content flex">
                            <span>
                                <img src="https://api.dicebear.com/9.x/glass/svg?seed=Alex"></img>
                            </span>
                            <div className="flex user-info">
                                <h2>{userInfo.name}</h2>
                                <h3> <MailCheckIcon size={16} className="icon" /> {userInfo.email} </h3>
                                <h3> <LucideUserCog2 size={16} className="icon" /> {userInfo.role}</h3>
                                <h4>{ checkDocumentStatus(userInfo.document) } </h4>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row flex">
                    <div className="c c1">
                        <div className="user-profile-card">
                            <h2 className="between">
                                <span>
                                    <FileTextIcon size={20} className="icon"/>Documento
                                </span>
                                <button className="btn purple" onClick={toggleModal}>
                                    <Upload size={12} />Upload 
                                </button>
                            </h2>

                             <ul className="item-list">
                                <li className="flex">
                                    <div className="left flex">
                                        Identidade
                                        <small>{userInfo.document && userInfo.document.updatedAt}</small>
                                    </div>
                                    { userInfo.document && checkDocumentStatus(userInfo.document) } 
                                </li>
                             </ul>
                        </div>
                    </div>
                    <div className="c c2">
                        <div className="user-profile-card">
                            <h2>
                                <Building2Icon size={20} className="icon"/>Organizacoes
                            </h2>

                            <ul className="item-list">
                                { userInfo && userInfo.organizationUsers && 
                                    userInfo.organizationUsers.map( (org, index) => (
                                        <li key={index} className="flex">
                                            { org.organization.organizationName }
                                                <span className={isActive(org.organization.isActive)}>
                                                { isActive(org.organization.isActive) }
                                            </span>
                                        </li>
                                    ))
                                }
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="row">
                        <div className="user-profile-card">
                            <h2>
                                <Wallet2Icon size={20} className="icon"/>Carteiras
                            </h2>

                            <ul className="item-list flex wallets">
                                {
                                    wallets && wallets.map( (wallet:any, index) => (
                                        <li key={index}>
                                            
                                            <div className="wallet-left">
                                                <div className="round">
                                                    { wallet.asset.symbol}
                                                </div>
                                                <div className="wallet-info">
                                                    { wallet.nickname }
                                                    <small>
                                                        <Calendar1Icon size={10} /> { wallet.updatedAt }
                                                    </small>
                                                </div>
                                            </div>

                                            <span className="value">
                                                { wallet.asset.symbol === 'R$' ? 'R$ ' : ''}
                                                { formatAssetAmount(wallet.balance, wallet.asset.symbol) }
                                            </span>

                                        </li>
                                    ))
                                }
                            </ul>

                            {/* Uma bolinha por carteira. Sao decorativas, entao a
                                contagem vai no aria-label — senao o leitor de tela
                                anuncia so elementos vazios. */}
                            { wallets && wallets.length > 0 && (
                                <div
                                    className="wallet-count"
                                    role="img"
                                    aria-label={`${wallets.length} ${wallets.length === 1 ? 'carteira' : 'carteiras'}`}
                                >
                                    { wallets.map( (wallet:any, index:number) => (
                                        <span className="dot" key={wallet.id ?? index} aria-hidden="true" />
                                    ))}
                                </div>
                            )}
                        </div>
                </div>

                <div className="row">
                    <div className="user-profile-card change-password">
                        <h2>
                            <KeyRound size={20} className="icon" />Trocar senha
                        </h2>

                        { userInfo?.usingDefaultPassword && (
                            <p className="default-password-warning">
                                Sua conta esta com a <b>senha padrao</b> definida pelo administrador.
                                Troque agora — ela e conhecida por toda a operacao.
                            </p>
                        )}

                        <div className="form-group password-field">
                            <label htmlFor="currentPassword">Senha atual</label>
                            <input
                                type={showCurrent ? "text" : "password"} id="currentPassword" name="currentPassword"
                                autoComplete="current-password"
                                value={passwordForm.current}
                                onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                                placeholder="Digite sua senha atual"
                            />
                            <button type="button" className="toggle" aria-label={showCurrent ? 'Ocultar senha' : 'Mostrar senha'}
                                onClick={() => setShowCurrent(v => !v)}>
                                { showCurrent ? <Eye size={18} /> : <EyeClosed size={18} /> }
                            </button>
                        </div>
                        <div className="form-group password-field">
                            <label htmlFor="newPassword">Nova senha</label>
                            <input
                                type={showNext ? "text" : "password"} id="newPassword" name="newPassword"
                                autoComplete="new-password"
                                value={passwordForm.next}
                                onChange={(e) => setPasswordForm(p => ({ ...p, next: e.target.value }))}
                                placeholder="Min. 8 caracteres, com letra e numero"
                            />
                            <button type="button" className="toggle" aria-label={showNext ? 'Ocultar senha' : 'Mostrar senha'}
                                onClick={() => setShowNext(v => !v)}>
                                { showNext ? <Eye size={18} /> : <EyeClosed size={18} /> }
                            </button>
                        </div>
                        <div className="form-group password-field">
                            <label htmlFor="confirmPassword">Confirme a nova senha</label>
                            <input
                                type={showConfirmPwd ? "text" : "password"} id="confirmPassword" name="confirmPassword"
                                autoComplete="new-password"
                                value={passwordForm.confirm}
                                onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                                placeholder="Digite a nova senha novamente"
                            />
                            <button type="button" className="toggle" aria-label={showConfirmPwd ? 'Ocultar senha' : 'Mostrar senha'}
                                onClick={() => setShowConfirmPwd(v => !v)}>
                                { showConfirmPwd ? <Eye size={18} /> : <EyeClosed size={18} /> }
                            </button>
                        </div>

                        { passwordError   && <span className="text-danger">{passwordError}</span> }
                        { passwordSuccess && <span className="text-success">{passwordSuccess}</span> }

                        <button className="btn purple" onClick={handleChangePassword} disabled={changingPassword}>
                            { changingPassword ? 'Salvando...' : 'Salvar nova senha' }
                        </button>
                    </div>
                </div>

                <div className="row">
                    <button className="btn logout" onClick={logout}>
                        Deslogar-se <LogOutIcon size={16} />
                    </button>
                </div>

            </div>

            <div className={`modal ${modalDocumentStatus ? 'active' : ''}`}>
                <MessageCircleXIcon size={36} onClick={toggleModal} className="modal-close"/>
                <div className="shadow"></div>
                <div className="modal-body">
                    { sendDocumentResponse && (
                        <div className="loader-wrapper">
                            Carregando Documento ...
                            <div className="loader"></div>
                        </div>
                    )}

                    <div className="content">
                        <h1>
                            Verificacao de Documento
                            <small>Faca o upload do documento para terminar</small>
                        </h1>

                        <div className="form">
                            
                            <div className="form-group">
                                <label htmlFor="document-type">Tipo:</label>
                                <select name="document-type" id="document-type" className="gray"
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}>
                                    <option value="">Selecione o documento</option>
                                    <option value="CNH">Carteira de motorista</option>
                                    <option value="PASSPORT">Passaporte</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="numero">Numero:</label>
                                <input type="text" id="numero" className="gray" placeholder="Digite o numero do documento" 
                                    value={documentNumber}
                                    onChange={(e) => setDocumentNumber(e.target.value)}/>
                            </div>

                            <div className="form-group">
                                <label htmlFor="upload">Upload do documento</label>
                                <input ref={fileInputRef} id="upload" type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
                                { !fileBase64 ? (
                                    <div className="flex upload" onClick={() => fileInputRef.current?.click()}>
                                        <UploadIcon size={36} />
                                        <span className="text-sm text-muted-foreground">Click to upload (max 10MB)</span>
                                    </div>
                                ) : (
                                    <div className="flex uploaded">
                                        <div className="flex items-center">
                                            <FileCheck className="text-primary" />
                                            <span className="truncate max-w-[200px]">{fileName}</span>
                                        </div>
                                        <button type="button" onClick={clearFile} className="no-effect">
                                            <CircleXIcon className="h-4 w-4 close" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button className="btn purple-neon" onClick={handleSubmit}>
                                Fazer Upload
                            </button>

                        </div>
                    </div>

                </div>
            </div>

        </div>
    )
}

export default UserProfile;